import type { ResponseError } from '../types'

import { camelizeJSON } from './common'

const checkHttpStatus = async (response: Response): Promise<Response> => {
  if (response.status >= 200 && response.status < 300) {
    return response
  }

  if (response.status === 401) {
    window.Turbolinks.visit(window.location.toString(), { action: 'replace' })
    return response
  }

  await handleServerError(response)

  return response
}

const handleBrowserError = (error: unknown): ResponseError => {
  // Handle network issues (e.g. cancelled request)
  // It is important that it is the first thing we check on the fetch method
  return {
    status: 0,
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    body: error?.toString ? error.toString() : '',
    ok: false,
    statusText: 'Network error. Please try again.',
  }
}

const handleServerError = async (response: Response): Promise<void> => {
  const text = await response.text()

  const rejectResponse = {
    status: response.status,
    ok: false,
    statusText: response.statusText,
    body: null,
  }

  // We don't know what is the format of response error
  // it might be a text or json
  // that's why we first try to parse it as json
  try {
    const json = JSON.parse(text) as Record<string, unknown>
    const camelizedJson = camelizeJSON(json)

    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    return Promise.reject({
      ...rejectResponse,
      body: camelizedJson,
    })
  } catch {
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    return Promise.reject({
      ...rejectResponse,
      body: text,
    })
  }
}

export const raw = async (
  url: URL | RequestInfo,
  options: RequestInit = {}
): Promise<Response> => {
  if (!window.fetch) {
    throw Error('Polyfill not working, no fetch method')
  }

  options.headers = {
    ...options.headers,
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRF-Token': window.Silverfin.csrfToken(),
  }

  options.method = options.method || 'GET'

  try {
    const response = await fetch(url, options)
    return checkHttpStatus(response)
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw handleBrowserError(e)
  }
}
